import React from "react";
import type { MRT_Row, MRT_TableInstance } from "material-react-table";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";

import type { Job } from "../../../../models";
import type { IAirtableAttachment } from "@rogoag/airtable";
import { FileDownloader } from "../../../../utilities/downloadFile";
import { Oops } from "../../../../utilities/oops";
import type { DownloadMenuContext } from "../JobsDashboardTable.types";
import { FILE_MENU_LIST } from "../JobsDashboardTable.types";
import {
	generateCSV,
	getDownloadItemName,
	getDownloadRowName,
	getJobIdFromRow,
	getFilesForMenuItem,
	getAllFilesForMenuItems,
} from "../JobsDashboardTable.helpers";

export interface UseFileDownloadsParams {
	jobs: Job[];
	selectedJobs: Job[];
	userEmail?: string;
	tableRef: React.RefObject<MRT_TableInstance<Job> | undefined>;
	actionMenuRow: MRT_Row<Job> | null;
}

export interface UseFileDownloadsReturn {
	downloadFiles: (
		jobIds: string[],
		files: IAirtableAttachment[],
		columnName: string,
		rowName: string,
		mergeCSV?: boolean,
	) => () => Promise<void>;
	exportCSV: () => Promise<void>;
	copyJobId: () => void;
	getDownloadMenuContext: () => DownloadMenuContext;
}

export function useFileDownloads({
	jobs,
	selectedJobs,
	userEmail,
	tableRef,
	actionMenuRow,
}: UseFileDownloadsParams): UseFileDownloadsReturn {
	function downloadFiles(
		jobIds: string[],
		files: IAirtableAttachment[],
		columnName: string,
		rowName: string,
		mergeCSV: boolean = false,
	) {
		return async () => {
			const toastDownloading = toast.loading("Downloading files...");

			try {
				await FileDownloader.downloadAttachments(files, getFileName(), files.length > 1, mergeCSV);
				Sentry.logger.info(`Downloaded ${columnName} file(s)`, {
					airtable_record_id: jobIds.join(","),
					username: userEmail,
				});
			} catch (error) {
				Oops.handleError(error, "JobsDashboardTable.downloadFiles", userEmail);
			} finally {
				toast.dismiss(toastDownloading);
			}

			function getFileName() {
				let fileName = "";
				if (files.length > 1) {
					fileName = rowName ? `${rowName} - ${columnName}` : "";
				}
				return fileName;
			}
		};
	}

	async function exportCSV() {
		try {
			const table = tableRef.current;
			if (!table) return;

			const toastLoading = toast.loading("Generating CSV...");
			const rows = table.getFilteredRowModel().rows;
			const excludedColumns = ["Files", "Expand", "Select", "Map Issue"];
			const columns = table.getAllColumns().filter((col) => !excludedColumns.includes(col.columnDef.header));

			const csv = generateCSV(columns, rows);

			const blob = new Blob([csv], { type: "text/csv" });
			const filename = `rogo_jobs_export_${new Date().toISOString().split("T")[0]}.csv`;
			FileDownloader.downloadBlob(blob, filename);

			toast.dismiss(toastLoading);
			toast.success(`Exported ${rows.length} rows to CSV`);

			Sentry.logger.info("CSV export from Jobs Dashboard", {
				username: userEmail,
				airtable_record_count: jobs.length,
				airtable_record_id: jobs.map((job) => job.id).join(","),
			});
		} catch (error) {
			Oops.handleError(error, "JobsDashboardTable.exportCSV", userEmail);
		}
	}

	function copyJobId() {
		const jobId = getJobIdFromRow(actionMenuRow, selectedJobs);
		navigator.clipboard
			.writeText(jobId)
			.then(() => {
				toast.success("Job ID copied to clipboard");
			})
			.catch(() => {
				toast.error("Failed to copy Job ID to clipboard");
			});
	}

	function getDownloadMenuContext(): DownloadMenuContext {
		const itemName = getDownloadItemName(actionMenuRow, selectedJobs);
		const jobId = getJobIdFromRow(actionMenuRow, selectedJobs);
		const allFiles = getAllFilesForMenuItems(FILE_MENU_LIST, actionMenuRow, selectedJobs, jobs);

		const fileItems = FILE_MENU_LIST.map((menuItem) => {
			const files = getFilesForMenuItem(menuItem, actionMenuRow, selectedJobs, jobs);
			const rowName = getDownloadRowName(actionMenuRow, selectedJobs);
			return { menuItem, files, rowName };
		});

		const showMergedCSV = !actionMenuRow || actionMenuRow?.getIsGrouped();
		const mergedCSVFiles = getFilesForMenuItem(
			{ label: "Sampling CSV", property: "labCSV" },
			actionMenuRow,
			selectedJobs,
			jobs,
		);

		return { itemName, jobId, allFiles, fileItems, showMergedCSV, mergedCSVFiles };
	}

	return {
		downloadFiles,
		exportCSV,
		copyJobId,
		getDownloadMenuContext,
	};
}
